/* Common Imports */

import React from "react";
import { styled } from "@mui/system";

/* MUI Imports */

import { Chip, Switch, TextField, Typography } from "@mui/material";

/* Component Imports */

import BrokerageSlider from "../BrokerageSlider";

/* Helper Imports */

import { brokerageOnToggle } from "../brokerage-config";

/* Styled Components */

const Container = styled("div")(() => ({
	display: "flex",
	flexDirection: "column",
	gap: "2rem",
}));

const SectionTitle = styled(Typography)(() => ({
	fontSize: "1rem",
	fontWeight: 600,
	color: "#212121",
}));

const ChipGroup = styled("div")(() => ({
	display: "flex",
	flexDirection: "row",
	gap: "0.5rem",
	flexWrap: "wrap",
	marginTop: "0.75rem",
}));

const BrokerageRow = styled("div")(() => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
}));

const SliderBox = styled("div")(() => ({
	marginTop: "0.75rem",
}));

/* Furnishing options */

const furnishingOptions = ["Furnished", "Semi-furnished", "Unfurnished"];

/* Possession timeline options */

const possessionOptions = ["Immediately", "7 Days", "15 Days", "30 Days", "90 Days", "Flexible"];

/* Component */

const AdditionalDetailsStep = ({
	formData,
	updateFormData,
}: {
	formData: any;
	updateFormData: (field: string, value: any) => void;
}) => {
	const noFurnishing = ["Plot", "Farm Plot", "Plot (Commercial)", "Land (Residential)", "Land (Commercial)", "Land (Agricultural)", "Land (Industrial)", "Co-working", "Warehouse", "Factory", "Mining", "Shop", "Office Space", "Building (Commercial)"];
	const types: string[] = formData.property_type || [];
	const showFurnishing = types.length > 0 && !types.some((t: string) => noFurnishing.includes(t));

	return (
		<Container>
			{/* Furnishing Preference */}

			{showFurnishing && (
			<div>
				<SectionTitle>What is your furnishing preference?</SectionTitle>
				<ChipGroup>
					{furnishingOptions.map((option) => (
						<Chip
							key={option}
							label={option}
							variant={formData.configuration.furnishing === option.toLowerCase().replace("-", "_") ? "filled" : "outlined"}
							onClick={() => updateFormData("configuration.furnishing", option.toLowerCase().replace("-", "_"))}
							sx={{
								backgroundColor:
									formData.configuration.furnishing === option.toLowerCase().replace("-", "_") ? "#FFF8F3" : "transparent",
								borderColor:
									formData.configuration.furnishing === option.toLowerCase().replace("-", "_") ? "#E07934" : "#e0e0e0",
								color: formData.configuration.furnishing === option.toLowerCase().replace("-", "_") ? "#E07934" : "#424242",
								fontWeight: 500,
							}}
						/>
					))}
				</ChipGroup>
			</div>
			)}

			{/* Possession Timeline */}

			<div>
				<SectionTitle>When do you plan to move?</SectionTitle>
				<ChipGroup>
					{possessionOptions.map((option) => (
						<Chip
							key={option}
							label={option}
							variant={formData.configuration.possession_by === option.toLowerCase().replace(" ", "_") ? "filled" : "outlined"}
							onClick={() => updateFormData("configuration.possession_by", option.toLowerCase().replace(" ", "_"))}
							sx={{
								backgroundColor:
									formData.configuration.possession_by === option.toLowerCase().replace(" ", "_") ? "#FFF8F3" : "transparent",
								borderColor:
									formData.configuration.possession_by === option.toLowerCase().replace(" ", "_") ? "#E07934" : "#e0e0e0",
								color:
									formData.configuration.possession_by === option.toLowerCase().replace(" ", "_") ? "#E07934" : "#424242",
								fontWeight: 500,
							}}
						/>
					))}
				</ChipGroup>
			</div>

			{/* Brokerage */}

			<div>
				<BrokerageRow>
					<div>
						<SectionTitle>Willing to pay brokerage?</SectionTitle>
						<Typography sx={{ fontSize: "0.75rem", color: "#616161", marginTop: "0.25rem" }}>
							This increases the chances of agents responding to your requirement.
						</Typography>
					</div>
					<Switch
						checked={formData.brokerage.is_offering_brokerage}
						onChange={(e) => updateFormData("brokerage", brokerageOnToggle(e.target.checked, formData.transaction_type))}
						sx={{
							"& .MuiSwitch-switchBase.Mui-checked": { color: "#2E7D32" },
							"& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#2E7D32" },
						}}
					/>
				</BrokerageRow>

				{/* Days of rent for rent, a percentage of the budget otherwise, the same slider as the newer flow and the app */}

				{formData.brokerage.is_offering_brokerage && (
					<SliderBox>
						<BrokerageSlider
							transactionType={formData.transaction_type}
							value={formData.brokerage.value}
							budgetMax={formData.budget.max}
							onChange={(next) => updateFormData("brokerage.value", next)}
							accentColor="#E07934"
							valueColor="#212121"
						/>
					</SliderBox>
				)}
			</div>

			{/* Notes */}

			<div>
				<SectionTitle>Notes</SectionTitle>
				<TextField
					fullWidth
					multiline
					rows={4}
					placeholder="Eg. prefer a quiet residential area with parking and power backup"
					value={formData.notes}
					onChange={(e) => updateFormData("notes", e.target.value)}
					inputProps={{ maxLength: 1000 }}
					sx={{ marginTop: "0.75rem" }}
				/>
			</div>
		</Container>
	);
};

export default AdditionalDetailsStep;
